import { Component, Input, OnInit, OnDestroy, TemplateRef, ApplicationRef, ElementRef, HostListener } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { MenuAction } from 'src/app/models/common/common.model';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ModalPdfViewerComponent } from '../../../../shared/modal-pdf-viewer/modal-pdf-viewer.component';
import {
  PDF_IMAGE_FORMAT,
  PDF_RENDER_SCALE,
  canvasToPdfImage,
  createReportPdf,
} from '../../../../shared/functions/pdf-report.util';
import html2canvas from 'html2canvas';

@Component({
  selector: 'app-report-section',
  templateUrl: './report-section.component.html',
  styleUrls: ['./report-section.component.css'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-4px)' }),
        animate('150ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
      transition(':leave', [
        animate('100ms ease-in', style({ opacity: 0, transform: 'translateY(-4px)' })),
      ]),
    ]),
  ],
})
export class ReportSectionComponent implements OnInit, OnDestroy {
  @Input() reportTemplate?: TemplateRef<any>;
  @Input() serviceData: any;
  @Input() orderHash = '';
  @Input() reportName = 'report.pdf';

  showMenu = false;
  reportMenuActions: MenuAction[] = [];

  private isProcessing = false;

  constructor(
    private bsModalService: BsModalService,
    private appRef: ApplicationRef,
    private elementRef: ElementRef
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.showMenu = false;
    }
  }

  ngOnInit(): void {
    this.reportMenuActions = [
      { icon: 'tuiIconEye', labelKey: 'report-section.view', action: () => this.openReportModal() },
      { icon: 'tuiIconDownload', labelKey: 'report-section.download', action: () => this.downloadPdf() },
    ];
  }

  ngOnDestroy(): void {
    this.hideLoader();
  }

  toggleMenu(): void {
    this.showMenu = !this.showMenu;
  }

  closeModal(): void {
  
  }

  async openReportModal(): Promise<void> {
    this.showMenu = false;
    if (!this.reportTemplate) return;

    this.bsModalService.show(ModalPdfViewerComponent, {
      class: 'modal-lg',
      initialState: {
        htmlTemplate: this.reportTemplate,
        pdfName: this.reportName || `${this.orderHash}_report.pdf`,
        downloadLabelKey: 'report-section.download',
        showTerms: true,
        downloadFn: () => this.downloadPdf(),
      },
    });
  }

  async downloadPdf(): Promise<void> {
    this.showMenu = false;
    if (!this.reportTemplate || this.isProcessing) return;

    this.isProcessing = true;
    this.showLoader();

    const { viewRef, container } = this.createRenderContainer();

    try {
      const blob = await this.renderPdfBlobFromContainer(container);
      if (blob) {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = this.reportName || `${this.orderHash}_service_report.pdf`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } finally {
      this.cleanup(viewRef, container);
      this.isProcessing = false;
    }
  }

  private createRenderContainer(): { viewRef: any; container: HTMLElement } {
    const viewRef = this.reportTemplate!.createEmbeddedView(null as any);
    this.appRef.attachView(viewRef);
    viewRef.detectChanges();

    const container = document.createElement('div');
    container.style.cssText =
      'position:fixed;left:-99999px;top:0;width:900px;height:auto;overflow:visible;pointer-events:none;z-index:-1;background:#fff;';
    document.body.appendChild(container);
    viewRef.rootNodes.forEach((node: Node) => container.appendChild(node));

    return { viewRef, container };
  }

  private async renderPdfBlobFromContainer(container: HTMLElement): Promise<Blob | null> {
    const pdfContent = container.querySelector('#pdf-content') as HTMLElement | null;
    if (!pdfContent) return null;

    const pages = pdfContent.getElementsByClassName('page');
    if (!pages?.length) return null;

    const pdf = createReportPdf();
    const pageWidth = pdf.internal.pageSize.getWidth();
    let pdfPageAdded = false;

    for (let i = 0; i < pages.length; i++) {
      const page = pages[i] as HTMLElement;
      page.style.display = 'block';
      try {
        const canvas = await html2canvas(page, {
          scale: PDF_RENDER_SCALE,
          useCORS: true,
          backgroundColor: '#ffffff',
          scrollY: -window.scrollY,
        });
        const imgData = canvasToPdfImage(canvas);
        const margin = 10;
        const usableWidth = pageWidth - margin * 2;
        const imgHeight = (canvas.height * usableWidth) / canvas.width;
        if (imgHeight > 0) {
          if (pdfPageAdded) pdf.addPage();
          pdf.addImage(imgData, PDF_IMAGE_FORMAT, margin, margin, usableWidth, imgHeight);
          pdfPageAdded = true;
        }
      } catch (_) {
      } finally {
        page.style.display = '';
      }
    }

    if (!pdfPageAdded) return null;

    try {
      return pdf.output('blob');
    } catch {
      try {
        const data = pdf.output('datauristring');
        const base64 = data.split(',')[1];
        const byteChars = atob(base64);
        const byteArray = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteArray[i] = byteChars.charCodeAt(i);
        }
        return new Blob([byteArray], { type: 'application/pdf' });
      } catch {
        return null;
      }
    }
  }

  getPaginatedPickupImages(): string[][] {
    return this.paginateImages(this.serviceData?.trackingData?.pickUpImages);
  }

  getPaginatedDropoffImages(): string[][] {
    return this.paginateImages(this.serviceData?.trackingData?.dropOffImages);
  }

  private paginateImages(images: string[] | undefined, pageSize = 6): string[][] {
    if (!images?.length) return [];
    const pages: string[][] = [];
    for (let i = 0; i < images.length; i += pageSize) {
      pages.push(images.slice(i, i + pageSize));
    }
    return pages;
  }

  private cleanup(viewRef: any, container: HTMLElement): void {
    this.hideLoader();
    try { this.appRef.detachView(viewRef); } catch (_) {}
    try { viewRef.destroy(); } catch (_) {}
    if (container.parentNode) container.parentNode.removeChild(container);
  }

  private showLoader(): void {
    const div = document.getElementById('loader');
    if (div) div.style.display = 'flex';
  }

  private hideLoader(): void {
    const div = document.getElementById('loader');
    if (div) div.style.display = 'none';
  }
}
